(function HotelEngineSearchFilter($, Adzuki)
{
	'use strict';

	const component = {
		emits: ['filtered-hotels', 'show-hotel-map', 'filter-list'],
		props: {
			hotels: {
				type    : Array,
				required: true,
				default : () => []
			},
			coordinates: {
				type: Object,
			},
			totalRooms: {
				type    : Number,
				required: true,
				default : 0
			},
			numNights: {
				type    : Number,
				required: true,
				default : 0
			},
			currentList: {
				type    : Array,
				required: false,
				default : () => []
			},
			showMap: {
				type    : Boolean,
				required: false,
				default : false
			},
			initialFilters: {
				type   : Object,
				default: null
			},
		},
		template: '#he-search-filters',
		data() {
			return {
				baseUri            : Adzuki.base_uri,
				amenitySearch      : [],
				amenityList        : [],
				collectionsSearch  : [],
				minSearchRate      : 0,
				maxSearchRate      : 0,
				propertySearch     : '',
				rateRangeSearch    : {},
				specialtyRateSearch: [],
				starSearch         : [],
				showFilterMap      : this.showMap,
				filters            : {},
				specialtyRateList  : {
					FHR: 'FHR - Fine Hotels &amp; Resorts',
					GCD: 'THC - The Hotel Collection',
					THC: 'THC - The Hotel Collection',
					SGP: 'SGP - Small &amp; Grand Properties',
					SIP: 'SIP - Signature Properties',
				},
				perNightPrice     : 0,
				sidebarMapInstance: null,
				sidebarMapDefaults: {
					lat : 0,
					lng : 0,
					zoom: 11
				},
				sharedFiltersApplied: false,
			};
		},
		computed: {
			// Only holds the criteria the user actually set; empty or missing values are left out
			// so every consumer can treat "key present" as "filter is active".
			activeFilters() {
				const active         = {};
				const propertyTerm   = this.toText(this.propertySearch);
				const rateRange      = this.toObject(this.rateRangeSearch);
				const perNightPrice  = this.toNumber(this.perNightPrice);
				const starSearch     = this.toList(this.starSearch);
				const specialtyRates = this.toList(this.specialtyRateSearch);
				const collections    = this.toList(this.collectionsSearch);
				const amenities      = this.toList(this.amenitySearch);

				if (propertyTerm) {
					active.propertySearch = propertyTerm;
				}

				if (rateRange.min || rateRange.max) {
					active.rateRangeSearch = {
						min: rateRange.min ? this.toNumber(rateRange.min) : this.minMaxPrices.min,
						max: rateRange.max ? this.toNumber(rateRange.max) : this.minMaxPrices.max
					};
				}

				if (perNightPrice > 0 && perNightPrice < this.sliderMax) {
					active.perNightPrice = perNightPrice;
				}

				if (starSearch.length > 0) {
					active.starSearch = starSearch;
				}

				if (specialtyRates.length > 0) {
					active.specialtyRateSearch = specialtyRates;
				}

				if (collections.length > 0) {
					active.collectionsSearch = collections;
				}

				if (amenities.length > 0) {
					active.amenitySearch = amenities;
				}

				return active;
			},
			filteredHotels() {
				const hotelsArray = this.toList(this.hotels);
				const active      = this.activeFilters;

				this.filters = { ...active };

				if (Object.keys(active).length === 0) {
					return this.moveUnpricedToBottom(hotelsArray);
				}

				return this.moveUnpricedToBottom(this.applyActiveFilters(hotelsArray, active));
			},
			minMaxPrices() {
				const hotelsArray = this.toList(this.hotels);
				if (!hotelsArray.length) {
					return {
						min: 0,
						max: 0
					};
				}

				const prices = hotelsArray
					.map(hotel => this.getHotelNightlyRate(hotel))
					.filter(
						price => price > 0
					);

				if (!prices.length) {
					return {
						min: 0,
						max: 0
					};
				}

				return {
					min: Math.min(...prices),
					max: Math.max(...prices)
				};
			},
			sliderMin() {
				return this.minMaxPrices.min;
			},
			sliderMax() {
				if (this.minMaxPrices.max > this.minMaxPrices.min) {
					return this.minMaxPrices.max;
				}
				return this.minMaxPrices.min + 1;
			},
			starRatings() {
				const starRatings = new Set();

				this.toList(this.hotels).forEach((hotel) => {
					const star = this.toNumber(hotel?.star_rating);
					if (!star && star !== 0) {
						return;
					}

					if ((star % 1) !== 0.5) {
						starRatings.add(star.toFixed(2));
					}
				});

				return Array.from(starRatings).sort((a, b) => parseFloat(b) - parseFloat(a));
			},
			hotelsForStarCounts() {
				const hotelsArray = this.toList(this.hotels);
				const active      = { ...this.activeFilters };

				delete active.starSearch;

				if (Object.keys(active).length === 0) {
					return hotelsArray;
				}

				return this.applyActiveFilters(hotelsArray, active);
			},
			starRatingCounts() {
				const counts = {};

				this.starRatings.forEach((rating) => {
					counts[rating] = 0;
				});

				this.hotelsForStarCounts.forEach((hotel) => {
					this.starRatings.forEach((rating) => {
						if (this.hotelMatchesStarRating(hotel, rating)) {
							counts[rating]++;
						}
					});
				});

				return counts;
			},
			collections() {
				const collections = new Map();

				this.toList(this.hotels).forEach((hotel) => {
					this.toList(hotel?.collections).forEach((collection) => {
						if (collection?.id) {
							collections.set(collection.id, collection.name);
						}
					});
				});
				return collections;
			},
			specialSales() {
				const specialSales = new Map();
				this.toList(this.hotels).forEach((hotel) => {
					this.toList(hotel?.specialty_rates).forEach((rate) => {
						if (!rate?.code) {
							return;
						}

						// Keep filter value as the real Sabre code (GCD); display maps to THC.
						// Prefer GCD when both appear so checkboxes stay filtered against hotel data.
						if (rate.code === 'THC' && specialSales.has('GCD')) {
							return;
						}
						if (rate.code === 'GCD' && specialSales.has('THC')) {
							specialSales.delete('THC');
						}
						specialSales.set(rate.code, rate.description);
					});
				});

				return specialSales;
			},
			sidebarMapImageUrl() {
				const lat = this.coordinates?.lat;
				const lng = this.coordinates?.lng;

				if (!lat || !lng) {
					return null;
				}

				const params = new URLSearchParams({
					center : `${lat},${lng}`,
					zoom   : 11,
					size   : '400x200',
					scale  : 2,
					maptype: 'roadmap',
					key    : 'AIzaSyBR_Lf_8TwJ6_fH8KEMOitSRTnrH-ia_kA',
				});

				return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
			}
		},
		methods: {
			// Hotel payloads send list data as an array, an object keyed by id, or nothing at all.
			toList(value) {
				if (Array.isArray(value)) {
					return value.filter((item) => item !== null && item !== undefined);
				}
				if (value && typeof value === 'object') {
					return Object.values(value).filter((item) => item !== null && item !== undefined);
				}
				return [];
			},
			toObject(value) {
				if (value && typeof value === 'object' && !Array.isArray(value)) {
					return value;
				}
				return {};
			},
			toText(value) {
				if (value === null || value === undefined) {
					return '';
				}
				return String(value).trim();
			},
			getHotelAmenityNames(hotel) {
				return this.toList(hotel?.metadata?.amenities)
					.map((amenity) => (typeof amenity === 'string' ? amenity : amenity?.mapped_name))
					.filter((name) => Boolean(name));
			},
			hotelMatchesPropertyName(hotel, term) {
				const nameMatch = this.toText(hotel?.name).toLowerCase().includes(term);

				if (nameMatch) {
					return true;
				}

				return this.toList(hotel?.alternate_names).some((alt) =>
					this.toText(alt?.name).toLowerCase().includes(term)
				);
			},
			applyActiveFilters(hotels, activeFilters) {
				const active = this.toObject(activeFilters);

				let results = this.toList(hotels);

				if (active.propertySearch) {
					const term = active.propertySearch.toLowerCase();
					results = results.filter((hotel) => this.hotelMatchesPropertyName(hotel, term));
				}

				if (active.rateRangeSearch) {
					results = results.filter((hotel) => {
						const rate = this.getHotelNightlyRate(hotel);
						return rate >= active.rateRangeSearch.min && rate <= active.rateRangeSearch.max;
					});
				}

				if (active.perNightPrice) {
					results = results.filter((hotel) =>
						this.getHotelNightlyRate(hotel) <= active.perNightPrice
					);
				}

				if (active.starSearch) {
					results = results.filter((hotel) =>
						active.starSearch.some((star) => this.hotelMatchesStarRating(hotel, star))
					);
				}

				if (active.specialtyRateSearch) {
					results = results.filter((hotel) =>
						this.toList(hotel?.specialty_rate_codes).some((hotelRate) =>
							this.hotelMatchesSpecialtyRate(hotelRate, active.specialtyRateSearch)
						)
					);
				}

				if (active.collectionsSearch) {
					results = results.filter((hotel) =>
						this.toList(hotel?.collections).some((collection) =>
							active.collectionsSearch.includes(collection?.id)
						)
					);
				}

				if (active.amenitySearch) {
					results = results.filter((hotel) => {
						const hotelAmenities = this.getHotelAmenityNames(hotel);

						if (!hotelAmenities.length) {
							return false;
						}

						return active.amenitySearch.every((selectedAmenity) =>
							hotelAmenities.includes(selectedAmenity)
						);
					});
				}

				return results;
			},
			displaySpecialtyRateCode(code) {
				return Adzuki.HotelEngine.displaySpecialtyRateCode(code);
			},
			hotelMatchesSpecialtyRate(hotelRate, selectedCodes) {
				const code = Adzuki.HotelEngine.resolveSpecialtyRateCode(hotelRate);

				if (!code || !Array.isArray(selectedCodes) || selectedCodes.length === 0) {
					return false;
				}

				if (selectedCodes.includes(code)) {
					return true;
				}

				// GCD and THC are the same Hotel Collection program.
				if (code === 'GCD' && selectedCodes.includes('THC')) {
					return true;
				}

				if (code === 'THC' && selectedCodes.includes('GCD')) {
					return true;
				}

				return false;
			},
			toNumber(value) {
				if (typeof value === 'number') {
					return Number.isFinite(value) ? value : 0;
				}
				if (typeof value === 'string') {
					const normalized = value.replace(/[^0-9.-]/g, '');
					const parsed = parseFloat(normalized);
					return Number.isFinite(parsed) ? parsed : 0;
				}
				return 0;
			},
			hasSellingPrice(hotel) {
				return this.toNumber(hotel?.hotel_pricing?.selling_price) > 0;
			},
			moveUnpricedToBottom(hotels) {
				const list = this.toList(hotels);
				const priced = [];
				const unpriced = [];

				list.forEach((hotel) => {
					if (this.hasSellingPrice(hotel)) {
						priced.push(hotel);
					} else {
						unpriced.push(hotel);
					}
				});

				return [...priced, ...unpriced];
			},
			hotelMatchesStarRating(hotel, rating) {
				const hotelStarRating = this.toNumber(hotel?.star_rating);
				const baseRating = this.toNumber(rating);
				const halfRating = baseRating + 0.5;

				return hotelStarRating === baseRating || hotelStarRating === halfRating;
			},
			getStarRatingCount(rating) {
				return this.starRatingCounts[rating] || 0;
			},
			getHotelNightlyRate(hotel) {
				const pricing = hotel?.hotel_pricing || {};
				const rawRate = pricing.price_per_night
					?? pricing.room_price_per_night
					?? pricing.best_search_rate
					?? pricing.selling_price
					?? pricing.market_rate
					?? 0;

				return this.toNumber(rawRate);
			},
			getUniqueAmenities(amenities) {
				const seen = new Set();

				return this.toList(amenities).filter(amenity => {
					const mappedId = amenity?.mapped_id;

					if (!mappedId || seen.has(mappedId)) {
						return false;
					}
					seen.add(mappedId);
					return true;
				});
			},
			setFeaturedSearch(collectionName, type) {
				const signatureCollections = ["SGP", "SIG", "SSP", "SCA"];

				if (type === 'specialty_rate') {
					if (signatureCollections.includes(collectionName)) {
						this.specialtyRateSearch = signatureCollections;
					} else {
						this.specialtyRateSearch.push(collectionName);
					}
				} else {
					this.collectionsSearch.push(collectionName);
				}
			},
			fetchAmenityList() {
				fetch(this.baseUri + '/HotelEngine/amenityList')
					.then((response) => {
						if (!response.ok) {
							throw new Error(`HTTP error! Status: ${response.status}`);
						}
						return response.json();
					})
					.then((data) => {
						if (data) {
							this.amenityList = this.toList(data);
						} else {
							console.error('Invalid data format from backend.');
						}
					})
					.catch((error) => {
						console.error('Error fetching hotels:', error);
					});
			},
			toggleMap() {
				this.$emit('show-hotel-map', this.showFilterMap);
			},
			clearFilters() {
				this.propertySearch = '';
				this.rateRangeSearch = {};
				this.starSearch = [];
				this.specialtyRateSearch = [];
				this.collectionsSearch = [];
				this.amenitySearch = [];
				this.minSearchRate = this.minMaxPrices.min;
				this.maxSearchRate = this.minMaxPrices.max;
				this.perNightPrice = this.sliderMax;
				this.filters = {};
				this.sharedFiltersApplied = false;
			},
			applySharedFilters(filters) {
				const sharedFilters = this.toObject(filters);

				if (Object.keys(sharedFilters).length === 0) {
					return;
				}

				const propertySearch      = this.toText(sharedFilters.propertySearch);
				const rateRangeSearch     = this.toObject(sharedFilters.rateRangeSearch);
				const perNightPrice       = this.toNumber(sharedFilters.perNightPrice);
				const starSearch          = this.toList(sharedFilters.starSearch);
				const specialtyRateSearch = this.toList(sharedFilters.specialtyRateSearch);
				const collectionsSearch   = this.toList(sharedFilters.collectionsSearch);
				const amenitySearch       = this.toList(sharedFilters.amenitySearch);

				if (propertySearch) {
					this.propertySearch = propertySearch;
				}

				if (rateRangeSearch.min || rateRangeSearch.max) {
					this.rateRangeSearch = { ...rateRangeSearch };
				}

				if (perNightPrice > 0) {
					this.perNightPrice = perNightPrice;
				}

				if (starSearch.length > 0) {
					this.starSearch = starSearch;
				}

				if (specialtyRateSearch.length > 0) {
					this.specialtyRateSearch = specialtyRateSearch;
				}

				if (collectionsSearch.length > 0) {
					this.collectionsSearch = collectionsSearch;
				}

				if (amenitySearch.length > 0) {
					this.amenitySearch = amenitySearch;
				}

				this.sharedFiltersApplied = true;

				this.$nextTick(() => {
					this.emitFilterList();
				});
			},
			emitFilterList() {
				const hasFilters = Object.keys(this.toObject(this.filters)).length > 0;
				const hasPendingSharedFilters = Boolean(
					Object.keys(this.toObject(this.initialFilters)).length > 0
					&& !this.sharedFiltersApplied
				);

				if (!hasFilters && hasPendingSharedFilters) {
					return;
				}

				this.$emit('filter-list', this.filters);
			}
		},
		watch: {
			filteredHotels: {
				flush: 'post',
				handler(newVal) {
					if (!this.toList(this.hotels).length) {
						return;
					}

					this.$emit('filtered-hotels', newVal);
					this.emitFilterList();
				}
			},
			currentList: {
				handler(newList) {
					this.toList(newList).forEach(filter => {
						if (!filter?.key) {
							return;
						}

						if (filter.key === 'rateRange') {
							this.rateRangeSearch = {};
							return;
						}

						if (filter.key === 'starSearch') {
							const rating = parseFloat(this.toText(filter.value).split(' ')[0]);

							if (Number.isNaN(rating)) {
								return;
							}

							const index = this.starSearch.indexOf(rating.toFixed(2));
							if (index > -1) {
								this.starSearch.splice(index, 1);
							}
							return;
						}

						if (!Array.isArray(this[filter.key])) {
							return;
						}

						const index = this[filter.key].indexOf(filter.value);
						if (index > -1) {
							this[filter.key].splice(index, 1);
						}
					});
				},
				deep: true
			},
			minMaxPrices() {
				this.minSearchRate = this.minMaxPrices.min;
				this.maxSearchRate = this.minMaxPrices.max;

				if (this.sharedFiltersApplied) {
					return;
				}

				this.perNightPrice = this.sliderMax;
			},
			hotels: {
				handler(newHotels, oldHotels) {
					const hadHotels = this.toList(oldHotels).length > 0;

					if (!hadHotels && this.toList(newHotels).length > 0 && this.initialFilters) {
						this.$nextTick(() => {
							this.applySharedFilters(this.initialFilters);
						});
					}
				}
			},
			showMap(newVal) {
				this.showFilterMap = newVal;
			},
			initialFilters: {
				deep: true,
				handler(filters) {
					if (this.toList(this.hotels).length > 0 && filters) {
						this.applySharedFilters(filters);
					}
				}
			},
		},
		mounted() {
			this.fetchAmenityList();

			if (this.initialFilters && this.toList(this.hotels).length > 0) {
				this.applySharedFilters(this.initialFilters);
			} else if (!this.initialFilters) {
				this.perNightPrice = this.sliderMax;
			}
		}
	};

	Adzuki.VueComponents.HotelEngineSearchFilter = component;
}(jQuery, window.Adzuki || {}));